import { useState, type FormEvent, type JSX } from 'react';
import type { BankQuestion, QuestionType } from '../api.ts';
import { numberOr } from '../lib/numbers.ts';

/**
 * Writing a question, and changing one already written.
 *
 * One form for both, and shared because it had to be: the form lived inside the question bank as
 * a private component, so a teacher inside a quiz could add a question or remove it but never fix
 * a typo in it. Fixing that by writing a second form would have given the two screens different
 * ideas of what a question is within a month.
 *
 * What can be changed narrows once pupils start answering. The text and the explanation stay
 * editable for ever; the options freeze, because a recorded answer points at an option by id and
 * rewriting the option would quietly change what a child is on record as having chosen. The
 * server refuses that, and the caller is expected to offer a copy instead.
 */

export const TYPE_LABELS: Record<QuestionType, string> = {
  single: 'Один вариант',
  multi: 'Несколько вариантов',
  text: 'Короткий ответ',
  number: 'Число',
  ordering: 'Порядок',
  fill: 'Пропуски',
  file: 'Файл',
  code: 'Код',
};

/** Types this form produces. File and code answers need a grader that does not exist yet. */
export const COMPOSABLE: QuestionType[] = ['single', 'multi', 'ordering', 'text', 'number'];

const NEEDS_OPTIONS: QuestionType[] = ['single', 'multi', 'ordering'];

/** Exactly what the create and update endpoints accept, so the caller does no reshaping. */
export interface QuestionDraft {
  type: QuestionType;
  text_md: string;
  explanation_md: string | null;
  config?: Record<string, unknown>;
  options?: { label: string; is_correct?: boolean }[];
}

const BLANK_OPTIONS = [
  { label: '', correct: true },
  { label: '', correct: false },
];

/** Pulls the editable values back out of a stored question. */
function seedFrom(question: BankQuestion | undefined): {
  type: QuestionType;
  text: string;
  explanation: string;
  options: { label: string; correct: boolean }[];
  accept: string;
  numberValue: string;
} {
  if (!question) {
    return {
      type: 'single',
      text: '',
      explanation: '',
      options: BLANK_OPTIONS,
      accept: '',
      numberValue: '',
    };
  }

  // config is whatever the question type stores; only these two shapes are ever read here.
  const config = question.config as { accept?: { value?: string }[]; value?: number | string };

  return {
    type: question.type,
    text: question.text_md,
    explanation: question.explanation_md ?? '',
    options:
      question.options.length > 0
        ? question.options.map((o) => ({ label: o.label, correct: o.is_correct }))
        : BLANK_OPTIONS,
    accept: Array.isArray(config.accept)
      ? config.accept.map((a) => String(a.value ?? '')).join('\n')
      : '',
    numberValue: config.value === undefined || config.value === null ? '' : `${config.value}`,
  };
}

export function QuestionForm({
  question,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  /** The question being changed, or nothing when writing a new one. */
  question?: BankQuestion;
  submitLabel: string;
  /** Throws to report a refusal; the caller decides what to say about it. */
  onSubmit: (draft: QuestionDraft) => Promise<void>;
  onCancel?: () => void;
}): JSX.Element {
  const seed = seedFrom(question);

  const [type, setType] = useState<QuestionType>(seed.type);
  const [text, setText] = useState(seed.text);
  const [explanation, setExplanation] = useState(seed.explanation);
  const [options, setOptions] = useState(seed.options);
  const [accept, setAccept] = useState(seed.accept);
  const [numberValue, setNumberValue] = useState(seed.numberValue);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const editing = question !== undefined;
  const withOptions = NEEDS_OPTIONS.includes(type);

  function setOption(index: number, patch: Partial<{ label: string; correct: boolean }>): void {
    setOptions(
      options.map((option, i) => {
        if (i === index) return { ...option, ...patch };
        // A single-choice question has exactly one key, so ticking one unticks the others.
        if (type === 'single' && patch.correct === true) return { ...option, correct: false };
        return option;
      }),
    );
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    setSaved(false);

    try {
      await onSubmit({
        type,
        text_md: text.trim(),
        explanation_md: explanation.trim() || null,
        ...(type === 'text'
          ? {
              config: {
                accept: accept
                  .split('\n')
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .map((value) => ({ value, mode: 'ci' })),
              },
            }
          : {}),
        ...(type === 'number' ? { config: { value: numberOr(numberValue, 0) } } : {}),
        ...(withOptions
          ? {
              options: options
                .filter((option) => option.label.trim())
                .map((option) => ({ label: option.label.trim(), is_correct: option.correct })),
            }
          : {}),
      });

      setSaved(true);

      if (!editing) {
        // A new question clears the form for the next one; an edited one keeps what was typed.
        setText('');
        setExplanation('');
        setAccept('');
        setNumberValue('');
        setOptions(BLANK_OPTIONS);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось сохранить вопрос');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={(e) => void submit(e)} style={{ marginBottom: 28 }}>
      <strong>{editing ? 'Правка вопроса' : 'Новый вопрос'}</strong>
      {error ? <p className="error">{error}</p> : null}

      <div className="field">
        <label htmlFor="q-type">Тип</label>
        <select
          id="q-type"
          className="input"
          value={type}
          /*
           * The type is fixed once the question exists. Turning a single-choice question into a
           * number one would leave every recorded answer pointing at options that no longer mean
           * anything, and nothing downstream could tell that from a legitimate edit.
           */
          disabled={editing}
          onChange={(e) => setType(e.target.value as QuestionType)}
          style={{ maxWidth: 260 }}
        >
          {COMPOSABLE.map((option) => (
            <option key={option} value={option}>
              {TYPE_LABELS[option]}
            </option>
          ))}
        </select>
        {editing ? (
          <span className="muted" style={{ fontSize: '0.82rem' }}>
            Тип вопроса менять нельзя — создайте новый.
          </span>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="q-text">Вопрос</label>
        <textarea
          id="q-text"
          className="input"
          rows={3}
          required
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Какой тип у значения `42`?"
        />
      </div>

      {withOptions ? (
        <div className="field">
          <label>
            {type === 'ordering'
              ? 'Варианты в правильном порядке — ученику они покажутся перемешанными'
              : 'Варианты ответа'}
          </label>
          <div className="stack">
            {options.map((option, index) => (
              <div key={index} className="row">
                {type !== 'ordering' ? (
                  <input
                    type={type === 'single' ? 'radio' : 'checkbox'}
                    name="correct"
                    checked={option.correct}
                    onChange={(e) => setOption(index, { correct: e.target.checked })}
                    aria-label={`Вариант ${index + 1} верный`}
                  />
                ) : (
                  <span className="ordering__pos">{index + 1}</span>
                )}
                <input
                  className="input input--inline"
                  value={option.label}
                  onChange={(e) => setOption(index, { label: e.target.value })}
                  placeholder={`Вариант ${index + 1}`}
                />
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  disabled={options.length <= 2}
                  onClick={() => setOptions(options.filter((_, i) => i !== index))}
                  aria-label="Убрать вариант"
                >
                  ✕
                </button>
              </div>
            ))}
            <div>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={options.length >= 20}
                onClick={() => setOptions([...options, { label: '', correct: false }])}
              >
                + Вариант
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {type === 'text' ? (
        <div className="field">
          <label htmlFor="q-accept">Что считать верным — по одному варианту в строке</label>
          <textarea
            id="q-accept"
            className="input"
            rows={3}
            value={accept}
            onChange={(e) => setAccept(e.target.value)}
            placeholder={'print\nprint()'}
          />
          <span className="muted" style={{ fontSize: '0.82rem' }}>
            Регистр не важен.
          </span>
        </div>
      ) : null}

      {type === 'number' ? (
        <div className="field">
          <label htmlFor="q-num">Верное значение</label>
          <input
            id="q-num"
            className="input"
            type="number"
            step="any"
            value={numberValue}
            onChange={(e) => setNumberValue(e.target.value)}
            style={{ maxWidth: 200 }}
          />
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="q-expl">Пояснение — покажется в разборе</label>
        <textarea
          id="q-expl"
          className="input"
          rows={2}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </div>

      <div className="saverow">
        <button
          className="btn btn--small"
          type="submit"
          disabled={busy || text.trim().length === 0}
        >
          {busy ? 'Сохраняем…' : submitLabel}
        </button>

        <span className="saverow__state" role="status" aria-live="polite">
          {saved ? <span className="saverow__done">✓ Сохранено</span> : null}
        </span>

        {onCancel ? (
          <button type="button" className="btn btn--ghost btn--small" onClick={onCancel}>
            Закрыть
          </button>
        ) : null}
      </div>
    </form>
  );
}
